import React, { useMemo, useState } from "react";
import GenericInput from "@ui-components/Input/GenericInput";
import { useTenantServices } from "./hooks";
import {
	filterServiceOptions,
	filterTenantOptions,
	groupServicesByTenant,
	pruneServicesToTenants,
	serviceDisplayCode,
	serviceKey,
	serviceTypeLabel,
	type ServiceGroup,
} from "./maintenanceWindowsModel";
import type { TenantOption, TenantServiceOption } from "./types";

/** How many customers the picker lists before asking for a narrower search. */
const VISIBLE_CUSTOMER_LIMIT = 50;

/**
 * A selectable row. A button rather than `GenericCheckbox`, whose clickable
 * `div` cannot be reached from the keyboard — same pattern as the service
 * list of `CreateSubscriptionModal`.
 */
function PickerOption({
	selected,
	onToggle,
	primary,
	secondary,
	trailing,
}: {
	selected: boolean;
	onToggle: () => void;
	primary: string;
	secondary?: string | null;
	trailing?: string;
}) {
	return (
		<li>
			<button
				type="button"
				onClick={onToggle}
				aria-pressed={selected}
				className={`w-full text-left flex items-baseline gap-2 px-3 py-2 rounded-xsm ${
					selected
						? "bg-semantic-bg-brand-secondary text-semantic-text-brand"
						: "hover:bg-semantic-state-active"
				}`}
			>
				<span
					aria-hidden="true"
					className={`self-center h-4 w-4 shrink-0 rounded-[3px] border ${
						selected
							? "border-semantic-border-brand bg-semantic-bg-brand-bold"
							: "border-semantic-border-secondary"
					}`}
				/>
				<span className="font-medium truncate">{primary}</span>
				{secondary && (
					<span className="text-sm text-semantic-text-secondary truncate">{secondary}</span>
				)}
				{trailing && (
					<span className="ml-auto text-xs text-semantic-text-secondary">{trailing}</span>
				)}
			</button>
		</li>
	);
}

function ListMessage({ children }: { children: React.ReactNode }) {
	return <li className="px-3 py-3 text-sm text-semantic-text-secondary">{children}</li>;
}

const LIST_CLASS =
	"list-none p-1 m-0 overflow-y-auto rounded-sm border border-semantic-border-primary";

/** One selected customer and its services. */
function ServiceGroupSection({
	group,
	selectedKeys,
	searchActive,
	onToggle,
	onSelectAll,
	onClear,
}: {
	group: ServiceGroup;
	selectedKeys: Set<string>;
	searchActive: boolean;
	onToggle: (service: TenantServiceOption) => void;
	onSelectAll: (services: TenantServiceOption[]) => void;
	onClear: (tenantId: number) => void;
}) {
	const chosen = group.services.filter((service) => selectedKeys.has(serviceKey(service)));
	return (
		<section className="flex flex-col gap-1">
			<div className="flex flex-wrap items-baseline gap-2 px-1">
				<span className="text-sm font-bold">{group.tenant.name}</span>
				{group.tenant.chargebeeCustomerId && (
					<span className="text-xs text-semantic-text-secondary">
						{group.tenant.chargebeeCustomerId}
					</span>
				)}
				{/* Stated, not implied: an empty selection is a choice with a
				    meaning, and it is the one customers will be told. */}
				<span className="text-xs text-semantic-text-secondary">
					{chosen.length === 0
						? "All services affected"
						: `${chosen.length} selected`}
				</span>
				<span className="ml-auto flex gap-3 text-xs">
					<button
						type="button"
						className="text-semantic-text-brand underline disabled:opacity-50"
						disabled={group.services.length === 0}
						onClick={() => onSelectAll(group.services)}
					>
						Select all
					</button>
					<button
						type="button"
						className="text-semantic-text-brand underline disabled:opacity-50"
						disabled={chosen.length === 0}
						onClick={() => onClear(group.tenant.id)}
					>
						Clear
					</button>
				</span>
			</div>
			<ul className={`${LIST_CLASS} max-h-44`}>
				{group.services.length === 0 && (
					<ListMessage>
						{searchActive
							? "No service of this customer matches the search."
							: "This customer has no Kontrol Edge services."}
					</ListMessage>
				)}
				{group.services.map((service) => (
					<PickerOption
						key={serviceKey(service)}
						selected={selectedKeys.has(serviceKey(service))}
						onToggle={() => onToggle(service)}
						primary={serviceDisplayCode(service)}
						secondary={service.serviceCode ? service.serviceName : null}
						trailing={serviceTypeLabel(service.serviceType)}
					/>
				))}
			</ul>
		</section>
	);
}

/**
 * Pick the affected customers, then — optionally — the affected services of
 * each (CTX-1294).
 *
 * Services are loaded for the selected customers only, so the list stays the
 * size of the notice rather than the size of the platform. Deselecting a
 * customer drops its services from the selection: the backend refuses a
 * service whose customer is not part of the window.
 */
export function CustomerServicePicker({
	tenantOptions,
	tenantsLoading,
	selectedTenants,
	selectedServices,
	onChange,
}: {
	tenantOptions: TenantOption[];
	tenantsLoading: boolean;
	selectedTenants: TenantOption[];
	selectedServices: TenantServiceOption[];
	onChange: (next: { tenants: TenantOption[]; services: TenantServiceOption[] }) => void;
}) {
	const [customerSearch, setCustomerSearch] = useState("");
	const [serviceSearch, setServiceSearch] = useState("");

	const selectedTenantIds = useMemo(
		() => selectedTenants.map((tenant) => tenant.id),
		[selectedTenants]
	);
	const services = useTenantServices(selectedTenantIds);

	const selectedTenantSet = useMemo(() => new Set(selectedTenantIds), [selectedTenantIds]);
	const selectedServiceKeys = useMemo(
		() => new Set(selectedServices.map(serviceKey)),
		[selectedServices]
	);

	const matchingTenants = useMemo(
		() => filterTenantOptions(tenantOptions, customerSearch),
		[tenantOptions, customerSearch]
	);
	const visibleTenants = matchingTenants.slice(0, VISIBLE_CUSTOMER_LIMIT);

	const groups = useMemo(
		() =>
			groupServicesByTenant(filterServiceOptions(services.data, serviceSearch), selectedTenants),
		[services.data, serviceSearch, selectedTenants]
	);

	const toggleTenant = (tenant: TenantOption) => {
		const tenants = selectedTenantSet.has(tenant.id)
			? selectedTenants.filter((selected) => selected.id !== tenant.id)
			: [...selectedTenants, tenant];
		onChange({
			tenants,
			services: pruneServicesToTenants(
				selectedServices,
				tenants.map((selected) => selected.id)
			),
		});
	};

	const toggleService = (service: TenantServiceOption) => {
		const key = serviceKey(service);
		onChange({
			tenants: selectedTenants,
			services: selectedServiceKeys.has(key)
				? selectedServices.filter((selected) => serviceKey(selected) !== key)
				: [...selectedServices, service],
		});
	};

	const selectAll = (candidates: TenantServiceOption[]) => {
		const missing = candidates.filter((service) => !selectedServiceKeys.has(serviceKey(service)));
		onChange({ tenants: selectedTenants, services: [...selectedServices, ...missing] });
	};

	const clearTenant = (tenantId: number) => {
		onChange({
			tenants: selectedTenants,
			services: selectedServices.filter((service) => service.tenantId !== tenantId),
		});
	};

	return (
		<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
			<div className="flex flex-col gap-2 min-w-0">
				<span className="text-sm font-medium text-semantic-text-primary">
					Affected customers
					<span className="ml-2 font-normal text-semantic-text-secondary">
						{selectedTenants.length} selected
					</span>
				</span>
				<GenericInput
					placeholder="Search by name or customer id (M01-…)"
					value={customerSearch}
					onChange={setCustomerSearch}
				/>
				<ul className={`${LIST_CLASS} max-h-72`}>
					{tenantsLoading && <ListMessage>Loading customers…</ListMessage>}
					{!tenantsLoading && visibleTenants.length === 0 && (
						<ListMessage>No customer matches the search.</ListMessage>
					)}
					{!tenantsLoading &&
						visibleTenants.map((tenant) => (
							<PickerOption
								key={tenant.id}
								selected={selectedTenantSet.has(tenant.id)}
								onToggle={() => toggleTenant(tenant)}
								primary={tenant.name}
								secondary={tenant.chargebeeCustomerId}
							/>
						))}
				</ul>
				{matchingTenants.length > VISIBLE_CUSTOMER_LIMIT && (
					<span className="text-sm text-semantic-text-secondary">
						More customers match — narrow the search to see them.
					</span>
				)}
			</div>

			<div className="flex flex-col gap-2 min-w-0">
				<span className="text-sm font-medium text-semantic-text-primary">
					Affected services
					<span className="ml-2 font-normal text-semantic-text-secondary">
						none selected for a customer = all of its services
					</span>
				</span>
				<GenericInput
					placeholder="Search by service id, name or type"
					value={serviceSearch}
					onChange={setServiceSearch}
					disabled={selectedTenants.length === 0}
				/>
				{services.error && (
					<p className="text-sm text-semantic-text-danger">{services.error}</p>
				)}
				{selectedTenants.length === 0 && (
					<p className="rounded-sm border border-semantic-border-primary px-3 py-3 text-sm text-semantic-text-secondary">
						Select a customer to list its services.
					</p>
				)}
				{selectedTenants.length > 0 && services.loading && (
					<p className="px-3 py-3 text-sm text-semantic-text-secondary">Loading services…</p>
				)}
				{selectedTenants.length > 0 && !services.loading && (
					<div className="flex flex-col gap-4 max-h-96 overflow-y-auto pr-1">
						{groups.map((group) => (
							<ServiceGroupSection
								key={group.tenant.id}
								group={group}
								selectedKeys={selectedServiceKeys}
								searchActive={serviceSearch.trim() !== ""}
								onToggle={toggleService}
								onSelectAll={selectAll}
								onClear={clearTenant}
							/>
						))}
					</div>
				)}
			</div>
		</div>
	);
}
